import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { log } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";

const POETIC_HOURS = [
  [0, 3,  "in the dead hour"],
  [3, 6,  "before first light"],
  [6, 9,  "as the world stirred"],
  [9, 12, "in the late morning"],
  [12, 15, "past noon"],
  [15, 18, "in the lengthening shadow"],
  [18, 21, "after sundown"],
  [21, 24, "in the small hours"],
];

function anomalyClass(severity) {
  if (severity === "high") return "border-[#721C1C] text-[#E3E1D9]";
  if (severity === "medium") return "border-[#A3A3A3] text-[#E3E1D9]";
  return "border-[#555] text-[#A3A3A3]";
}

const ACTION_LABEL = {
  "auth.login": "login",
  "site.update": "manuscript edit",
  "layers.update": "strata edit",
  "folder.update": "folder seal broken",
  "entity.create": "entity inscribed",
  "entity.update": "entity edited",
  "entity.delete": "entity erased",
  "reality.create": "reality inscribed",
  "reality.update": "reality edited",
  "reality.delete": "reality severed",
  "antecessor.update": "antecessor touched",
  "admin.add": "archivist inscribed",
  "admin.remove": "archivist struck",
  "settings.update": "seals reforged",
  "intruder_force.enabled": "trapdoor armed",
  "intruder_force.disabled": "trapdoor sealed",
  "intruder_force.entry": "intruder slipped in",
  "intruder_force.denied": "trapdoor rebuffed",
};

function poeticTime(iso) {
  try {
    const d = new Date(iso);
    const h = d.getUTCHours();
    for (const [lo, hi, phrase] of POETIC_HOURS) {
      if (h >= lo && h < hi) return phrase;
    }
  } catch (err) {
    log.warn("[VESPER] poeticTime parse failed:", err.message);
  }
  return "at an unknown hour";
}

function isoDate(iso) {
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return ""; }
}

function relativeTime(iso) {
  if (!iso) return "—";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
  } catch { return "—"; }
}

function GateScreen() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="vesper-gate">
      <div className="w-full max-w-md border border-[#333] p-8 sm:p-10 bg-[#0a0a0a] space-y-7">
        <div className="space-y-3">
          <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#A3A3A3]">
            VESPER · Daemon Threshold
          </div>
          <div className="flex items-center gap-3">
            <span className="tiv-stamp">SEALED</span>
            <span className="text-[0.55rem] tracking-[0.3em] uppercase text-[#555]">
              audit channel offline
            </span>
          </div>
          <h1 className="font-serif-display text-3xl tracking-tighter">
            Daemon Sleeping
          </h1>
          <p className="text-xs text-[#555] leading-relaxed">
            VESPER reports only to verified hands. Identify yourself at the archivist
            threshold to wake the daemon.
          </p>
        </div>
        <Link to="/admin" className="tiv-btn w-full text-center inline-block" data-testid="vesper-login-cta">
          Present the Key
        </Link>
        <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555] text-center pt-2 border-t border-[#333]">
          <Link to="/" className="hover:text-[#E3E1D9]">↩ Return to surface</Link>
        </div>
      </div>
    </div>
  );
}

function Entry({ e, delay }) {
  return (
    <article
      className="border-b border-[#222] py-5 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 chronicle-entry"
      style={{ animationDelay: `${delay}ms` }}
      data-testid="chronicle-entry"
    >
      <div className="lg:col-span-3 flex lg:flex-col gap-2 lg:gap-1">
        <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555]">
          {isoDate(e.timestamp)}
        </div>
        <div className="text-xs text-[#A3A3A3] italic font-serif-display">
          {poeticTime(e.timestamp)}
        </div>
        <div className="text-[0.55rem] tracking-[0.3em] uppercase text-[#444]">
          {relativeTime(e.timestamp)}
        </div>
      </div>
      <div className="lg:col-span-9">
        <p className="text-sm sm:text-base leading-relaxed font-mono-data">
          <span className="text-[#721C1C] tracking-[0.2em]">[VESPER]</span>
          <span className="text-[#A3A3A3]"> observation · </span>
          <span className="text-[#E3E1D9] tracking-[0.2em]">{e.actor}</span>
          {e.actor_role === "master" && (
            <span className="ml-1 text-[#721C1C]" title="master key">◈</span>
          )}
          <span className="text-[#A3A3A3]"> {e.narrative}</span>
          {e.target_obscured && (
            <>
              <span className="text-[#A3A3A3]"> </span>
              <span className="tiv-redact-soft">{e.target_obscured}</span>
            </>
          )}
          <span className="text-[#555]">.</span>
        </p>
      </div>
    </article>
  );
}

function InsightsPanel({ insights }) {
  if (!insights) return null;
  const statusColor = {
    listening: "text-[#E3E1D9]",
    vigil: "text-[#721C1C] tiv-blink",
    idle: "text-[#555]",
  }[insights.bot_status] || "text-[#E3E1D9]";

  return (
    <section className="border border-[#333] bg-[#0a0a0a] p-6 space-y-6" data-testid="vesper-insights">
      <header className="flex flex-wrap items-baseline justify-between gap-4 border-b border-[#333] pb-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="font-serif-display text-3xl tracking-tighter">{insights.bot_name}</span>
            <span className={`text-[0.7rem] tracking-[0.35em] uppercase ${statusColor}`}>
              ● {insights.bot_status}
            </span>
          </div>
          <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#555] mt-1">
            {insights.bot_designation}
          </div>
        </div>
        <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#A3A3A3] text-right space-y-1">
          <div>last activity · <span className="text-[#E3E1D9]">{relativeTime(insights.latest_timestamp)}</span></div>
          <div>server · <span className="text-[#E3E1D9]">{insights.server_time?.slice(11, 19)} UTC</span></div>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label="last 24h" value={insights.totals.last_24h} />
        <Stat label="last 7d" value={insights.totals.last_7d} />
        <Stat label="all time" value={insights.totals.all_time} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555] mb-2">
            Most active hands · 7d
          </div>
          <div className="space-y-1">
            {(insights.top_actors || []).length === 0 ? (
              <div className="text-[#555] text-xs">::: silent week :::</div>
            ) : (
              insights.top_actors.map((a) => (
                <div key={a.actor} className="flex justify-between text-xs tracking-[0.2em]" data-testid={`vesper-actor-${a.actor}`}>
                  <span className="text-[#E3E1D9]">{a.actor}</span>
                  <span className="text-[#A3A3A3]">{a.count}</span>
                </div>
              ))
            )}
          </div>
        </div>
        <div>
          <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555] mb-2">
            Frequent rites · 7d
          </div>
          <div className="space-y-1">
            {(insights.top_actions || []).length === 0 ? (
              <div className="text-[#555] text-xs">::: no rites recorded :::</div>
            ) : (
              insights.top_actions.map((a) => (
                <div key={a.action} className="flex justify-between text-xs">
                  <span className="text-[#E3E1D9] tracking-wide">{ACTION_LABEL[a.action] || a.action}</span>
                  <span className="text-[#A3A3A3] tracking-[0.2em]">{a.count}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div>
        <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555] mb-2">
          Anomalies · last hour
        </div>
        {(insights.anomalies || []).length === 0 ? (
          <div className="text-xs text-[#A3A3A3] font-mono-data">
            <span className="text-[#E3E1D9]">[VESPER]</span> nothing untoward. all rites within tolerance.
          </div>
        ) : (
          <div className="space-y-2">
            {insights.anomalies.map((a, i) => (
              <div
                key={a.code ? `${a.code}-${i}` : i}
                className={`text-xs font-mono-data border-l-2 pl-3 py-1 ${anomalyClass(a.severity)}`}
                data-testid={`vesper-anomaly-${a.code}`}
              >
                <span className={a.severity === "high" ? "text-[#721C1C]" : "text-[#A3A3A3]"}>
                  [{a.severity.toUpperCase()}]
                </span>{" "}
                <span className="tracking-[0.2em] uppercase text-[#555]">{a.code}</span>
                <div>{a.message}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[#222] p-4 bg-[#050505]">
      <div className="text-[0.55rem] tracking-[0.4em] uppercase text-[#555]">{label}</div>
      <div className="font-serif-display text-4xl tracking-tighter">{value ?? "—"}</div>
      <div className="text-[0.55rem] tracking-[0.3em] uppercase text-[#A3A3A3]">audit rites</div>
    </div>
  );
}

export default function Chronicle() {
  const { isAdmin, user } = useAuth();
  const [entries, setEntries] = useState(null);
  const [insights, setInsights] = useState(null);
  const [actorFilter, setActorFilter] = useState("");
  const [limit, setLimit] = useState(50);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = () => {
    api.get("/chronicle", { params: { limit } }).then((r) => setEntries(r.data));
    api.get("/chronicle/insights").then((r) => setInsights(r.data));
  };

  useEffect(() => {
    if (user === null) return; // auth still resolving
    if (!isAdmin) return;
    load();
  }, [user, isAdmin, limit]);

  useEffect(() => {
    if (!autoRefresh || !isAdmin) return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [autoRefresh, isAdmin, limit]);

  const filtered = useMemo(() => {
    if (!entries) return [];
    if (!actorFilter) return entries;
    return entries.filter((e) => e.actor.toUpperCase().includes(actorFilter.toUpperCase()));
  }, [entries, actorFilter]);

  if (user === null) {
    return <div className="text-[#555] text-xs tracking-widest uppercase">::: contacting daemon :::</div>;
  }
  if (!isAdmin) return <GateScreen />;

  return (
    <div className="space-y-10" data-testid="chronicle-page">
      <header className="space-y-6 max-w-3xl">
        <div className="flex items-center gap-3 text-[0.6rem] tracking-[0.4em] uppercase text-[#A3A3A3]">
          <span>Section V · Internal Audit Daemon</span>
          <span className="h-px flex-1 bg-[#333]" />
          <span className="tiv-blink text-[#721C1C]">REC</span>
        </div>
        <h1 className="font-serif-display text-5xl sm:text-6xl tracking-tighter leading-none">
          VESPER
        </h1>
        <p className="text-sm text-[#A3A3A3] leading-relaxed font-serif-display italic">
          Audit daemon. Watches the rolls so the archivists need not. Every login, every seal broken,
          every name struck — passes through this channel before any other eye sees it.
        </p>
        <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555]">
          channel open for <span className="text-[#E3E1D9]">{user?.username}</span> · alias <span className="text-[#E3E1D9]">{user?.alias}</span>
          {user?.role === "master" && <span className="text-[#721C1C] ml-2">◈ master key</span>}
        </div>
      </header>

      <InsightsPanel insights={insights} />

      <section data-testid="vesper-stream">
        <div className="section-label">
          <span>Observation Stream</span>
          <span>{filtered.length} / {entries?.length || 0}</span>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-4">
          <input
            className="tiv-input uppercase sm:max-w-xs"
            placeholder="filter by hand · A-XXX"
            value={actorFilter}
            onChange={(e) => setActorFilter(e.target.value)}
            data-testid="chronicle-actor-filter"
          />
          <select
            className="tiv-input sm:max-w-[160px]"
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value, 10))}
            data-testid="chronicle-limit"
          >
            <option value={25} className="bg-[#0a0a0a]">last 25</option>
            <option value={50} className="bg-[#0a0a0a]">last 50</option>
            <option value={100} className="bg-[#0a0a0a]">last 100</option>
            <option value={200} className="bg-[#0a0a0a]">last 200</option>
          </select>
          <label className="text-[0.65rem] tracking-[0.3em] uppercase text-[#A3A3A3] inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="accent-[#E3E1D9]"
              data-testid="chronicle-autorefresh"
            />
            live · 30s tick
          </label>
          <button className="tiv-btn sm:ml-auto" onClick={load} data-testid="chronicle-refresh">
            ↻ Tick
          </button>
        </div>

        {renderEntries(entries, filtered)}
      </section>

      <div className="border-t border-[#333] pt-6 text-[0.6rem] tracking-[0.4em] uppercase text-[#555] text-center">
        <Link to="/" className="hover:text-[#E3E1D9]">↩ Return to surface</Link>
      </div>
    </div>
  );
}

function renderEntries(entries, filtered) {
  if (entries === null) {
    return <div className="text-[#555] text-xs tracking-widest uppercase">::: VESPER listening :::</div>;
  }
  if (filtered.length === 0) {
    return (
      <div className="border border-dashed border-[#333] p-16 text-center text-[#555] text-xs tracking-widest uppercase">
        ::: no movement in this window :::
      </div>
    );
  }
  return (
    <div className="border-t border-[#222]">
      {filtered.map((e, i) => (
        <Entry key={`${e.timestamp}-${i}`} e={e} delay={Math.min(i, 12) * 40} />
      ))}
    </div>
  );
}
